import { useId, useMemo } from 'react';
import styled from 'styled-components';
import type { GroupRateObject } from '@shared/api';
import { Combobox, type ComboboxOption } from '@shared/ui/Combobox';
import { Control, FieldLabel, SegmentButton, Segmented, fleetType } from '@entities/fleet';
import type { RatesScope } from './scope';

const NARROW = '640px';

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
`;

const Field = styled.div<{ $grow?: boolean }>`
  flex: ${({ $grow }) => ($grow ? '1 1 320px' : '0 0 auto')};
  min-width: 0;

  @container rates (max-width: ${NARROW}) {
    flex: 1 1 100%;
  }
`;

/** Переключатель «чьи ставки» — высотой с поля рядом. */
const ScopeSwitch = styled(Segmented)`
  height: ${({ theme }) => theme.spacing(10)};

  & > button {
    font-size: ${fleetType.body};
  }

  @container rates (max-width: ${NARROW}) {
    display: flex;
    width: 100%;

    & > button {
      flex: 1 1 0;
      white-space: normal;
    }
  }
`;

const DateControl = styled(Control)`
  min-width: ${({ theme }) => theme.spacing(42)};
  font-variant-numeric: tabular-nums;
`;

interface RatesToolbarProps {
  scope: RatesScope;
  objects: readonly GroupRateObject[];
  objectsLoading: boolean;
  onScopeKind: (kind: 'project' | 'default') => void;
  onObject: (id: string | null) => void;
  on: string;
  onDate: (value: string) => void;
}

/** Чьи ставки (объект или по умолчанию для холдинга) и на какую дату. */
export function RatesToolbar({
  scope,
  objects,
  objectsLoading,
  onScopeKind,
  onObject,
  on,
  onDate,
}: RatesToolbarProps) {
  const uid = useId();
  const options = useMemo<ComboboxOption[]>(
    () =>
      objects.map((o) => ({
        id: o.id,
        label: o.name,
        sublabel: [o.code, o.status === 'archived' ? 'в архиве' : null]
          .filter((s): s is string => Boolean(s))
          .join(' · '),
      })),
    [objects],
  );
  const selected =
    scope.kind === 'project' ? objects.find((o) => o.id === scope.projectId) : undefined;
  const selectedLabel =
    scope.kind === 'project'
      ? selected
        ? [selected.code, selected.name].filter(Boolean).join(' · ')
        : objectsLoading
          ? 'Объект…'
          : 'Объект не найден'
      : undefined;

  return (
    <Bar role="group" aria-label="Чьи ставки и на какую дату">
      <Field>
        <ScopeSwitch role="group" aria-label="Чьи ставки">
          <SegmentButton
            type="button"
            aria-pressed={scope.kind !== 'default'}
            onClick={() => onScopeKind('project')}
          >
            Объект
          </SegmentButton>
          <SegmentButton
            type="button"
            aria-pressed={scope.kind === 'default'}
            onClick={() => onScopeKind('default')}
          >
            По умолчанию для холдинга
          </SegmentButton>
        </ScopeSwitch>
      </Field>
      {scope.kind !== 'default' && (
        <Field $grow>
          <FieldLabel htmlFor={`${uid}-object`}>Объект</FieldLabel>
          <Combobox
            id={`${uid}-object`}
            value={scope.kind === 'project' ? scope.projectId : ''}
            selectedLabel={selectedLabel}
            options={options}
            onChange={(o) => onObject(o ? o.id : null)}
            placeholder={objectsLoading ? 'Загружаю объекты…' : 'Название или код объекта'}
            loading={objectsLoading}
            emptyText="Такого объекта нет"
            disabled={objectsLoading && objects.length === 0}
          />
        </Field>
      )}
      <Field>
        <FieldLabel htmlFor={`${uid}-on`}>На дату</FieldLabel>
        <DateControl
          id={`${uid}-on`}
          type="date"
          value={on}
          onChange={(e) => onDate(e.target.value)}
        />
      </Field>
    </Bar>
  );
}
